/**
 * DataTable - Tabla de datos con MUI DataGrid
 * Wrapper con funcionalidades comunes: paginación, búsqueda, exportación
 */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon, FileDownload, Refresh } from '@mui/icons-material';
import { parseApiDate } from '../../utils/formatters';

const DataTable = ({
  columns = [],
  data = [],
  onEdit,
  onDelete,
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No hay datos para mostrar',
  renderCustomActions,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrar datos según búsqueda
  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;

    return columns.some((column) => {
      if (column.searchable === false) return false;

      const value = column.field.split('.').reduce((obj, key) => obj?.[key], row);
      return String(value || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  });

  // Paginar datos
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderCellValue = (row, column) => {
    const value = column.field.split('.').reduce((obj, key) => obj?.[key], row);

    // Renderizado personalizado con renderCell (recibe el row completo)
    if (column.renderCell) {
      return column.renderCell(row);
    }

    // Renderizado personalizado con render (recibe valor y row)
    if (column.render) {
      return column.render(value, row);
    }

    // Renderizado de estado activo/inactivo
    if (column.type === 'status' && typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'Activo' : 'Inactivo'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      );
    }

    // Renderizado de moneda
    if (column.type === 'currency' && typeof value === 'number') {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(value);
    }

    // Renderizado de fecha
    if (column.type === 'date' && value) {
      const parsed = parseApiDate(value);
      return parsed ? parsed.toLocaleDateString('es-AR') : '-';
    }

    return value || '-';
  };

  return (
    <Paper>
      {/* Búsqueda */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      {/* Tabla */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || 'left'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {column.headerName}
                </TableCell>
              ))}
              {(onEdit || onDelete || renderCustomActions) && (
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ py: 4 }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow key={row._id || row.id || index} hover>
                  {columns.map((column) => (
                    <TableCell key={column.field} align={column.align || 'left'}>
                      {renderCellValue(row, column)}
                    </TableCell>
                  ))}
                  {(onEdit || onDelete || renderCustomActions) && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {renderCustomActions && renderCustomActions(row)}
                        {onEdit && (
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(row)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(row)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {filteredData.length > 0 && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      )}
    </Paper>
  );
};

export default DataTable;
